import { useId, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { parseDecimal } from "@/lib/decimal";
import FieldError from "./FieldError";
import SettingsField from "./SettingsField";
import SettingsSection from "./SettingsSection";
import { fieldId, MAX_SEED_LOAD, UNIT_OPTIONS } from "./settingsForm";
import { formatDay, formatNumber } from "../lib/format";

const ROLE_GROUPS = [
  { role: "main", label: "Main lifts" },
  { role: "accessory", label: "Accessories" },
  { role: "shoulder_health", label: "Shoulder health" },
];

const loadWord = (exercise) => (exercise?.load_kind === "bodyweight" ? "Added weight" : "Load");

/**
 * Seeds: a weight you know you can work with, per exercise, so its first
 * plan starts there instead of "Find your weight" (spec §5.7). The server
 * merges seeds per key, so the page sends only what changed — a removed
 * one as null. Band moves have no load to seed and aren't offered.
 */
function SeedsSection({ seeds, settings, exercises, units, onChange, errorFor }) {
  const savedSeeds = settings?.seeded_loads ?? {};
  const timeZone = settings?.timezone;
  const byKey = useMemo(
    () => Object.fromEntries((exercises ?? []).map((e) => [e.key, e])),
    [exercises],
  );
  const nameOf = (key) => byKey[key]?.name ?? key;
  const seeded = new Set(seeds.map((row) => row.key));
  const groups = ROLE_GROUPS.map((group) => ({
    ...group,
    options: (exercises ?? [])
      .filter((e) => e.role === group.role && !e.archived && e.load_kind !== "band" && !seeded.has(e.key))
      .sort((a, b) => a.name.localeCompare(b.name)),
  })).filter((group) => group.options.length > 0);
  const removed = Object.entries(savedSeeds).filter(([key, seed]) => seed && !seeded.has(key));

  const [pick, setPick] = useState("");
  const [load, setLoad] = useState("");
  const [pickUnits, setPickUnits] = useState(null);
  const [addErrors, setAddErrors] = useState({});
  const [added, setAdded] = useState("");
  const newUnits = pickUnits ?? units;
  const ids = useId();
  const newId = (part) => `training-settings-seed-new-${part}-${ids.replace(/:/g, "")}`;

  const setRow = (key, field, value) => {
    setAdded("");
    onChange(seeds.map((row) => (row.key === key ? { ...row, [field]: value } : row)));
  };
  const remove = (key) => {
    setAdded("");
    onChange(seeds.filter((row) => row.key !== key));
  };
  const keep = (key) => {
    const seed = savedSeeds[key];
    onChange([
      ...seeds,
      { key, load: formatNumber(seed.load), units: seed.units ?? units, set_at: seed.set_at ?? null },
    ]);
  };
  const add = () => {
    const value = parseDecimal(load);
    const errors = {};
    if (!pick) errors.exercise = "Pick an exercise first.";
    if (value === null || value < 0 || value > MAX_SEED_LOAD) {
      errors.load = `Type the ${loadWord(byKey[pick]).toLowerCase()} in ${newUnits}, from 0 to ${MAX_SEED_LOAD} — like ${newUnits === "kg" ? "60" : "135"}.`;
    }
    setAddErrors(errors);
    if (Object.keys(errors).length) return;
    onChange([...seeds, { key: pick, load: formatNumber(value), units: newUnits, set_at: null }]);
    setAdded(`Added ${nameOf(pick)} at ${formatNumber(value)} ${newUnits} — it saves with the rest.`);
    setPick("");
    setLoad("");
    setPickUnits(null);
  };

  const rowStatus = (row) => {
    const was = savedSeeds[row.key];
    if (!was) return "New — saves with the rest.";
    if (row.units !== was.units || parseDecimal(row.load) !== Number(was.load)) {
      return "Changed — saves with the rest.";
    }
    const day = was.set_at ? formatDay(was.set_at, timeZone) : "";
    return day ? `Set on ${day}.` : "Saved.";
  };

  return (
    <SettingsSection
      id="training-settings-seeds"
      title="Seeds"
      error={errorFor("seeded_loads") || undefined}
      description={
        <>
          <p>
            A weight you know you can work with, so the first plan starts there instead of
            “Find your weight”. A seed counts as a set at the plan&apos;s target reps and reserve;
            once you log a newer set of that exercise, your sets take over.
          </p>
          <p>
            For chin-ups and other bodyweight moves the seed is the added weight (0 for none).
            Band moves don&apos;t use seeds.
          </p>
        </>
      }
    >
      {seeds.length === 0 ? (
        <p className="m-0 text-sm text-muted-foreground">
          No seeds. Exercises with no history start at “Find your weight”.
        </p>
      ) : (
        <ul aria-label="Your seeds" className="m-0 flex list-none flex-col gap-3 p-0">
          {seeds.map((row) => {
            const name = nameOf(row.key);
            const word = loadWord(byKey[row.key]);
            const unitsId = fieldId(`seeded_loads.${row.key}.units`);
            return (
              <li key={row.key} className="rounded-md border p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="m-0 font-medium">{name}</p>
                    <p className="m-0 text-xs text-muted-foreground">{rowStatus(row)}</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-11 shrink-0 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive md:h-8"
                    aria-label={`Remove the ${name} seed`}
                    title={`Remove the ${name} seed`}
                    onClick={() => remove(row.key)}
                  >
                    <Trash2 aria-hidden="true" />
                    Remove
                  </Button>
                </div>
                <div className="mt-2 grid grid-cols-[minmax(0,1fr)_7rem] items-start gap-3 sm:max-w-sm">
                  <SettingsField
                    id={fieldId(`seeded_loads.${row.key}.load`)}
                    label={
                      <>
                        {word}
                        <span className="sr-only"> for {name}</span>
                      </>
                    }
                    value={row.load}
                    onChange={(value) => setRow(row.key, "load", value)}
                    error={errorFor(`seeded_loads.${row.key}.load`) || undefined}
                  />
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor={unitsId}>
                      Units<span className="sr-only"> for {name}</span>
                    </Label>
                    <NativeSelect
                      id={unitsId}
                      value={row.units}
                      onChange={(event) => setRow(row.key, "units", event.target.value)}
                      aria-invalid={errorFor(`seeded_loads.${row.key}.units`) ? true : undefined}
                      className="h-11 md:h-9"
                    >
                      {UNIT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.value}
                        </option>
                      ))}
                    </NativeSelect>
                    <FieldError id={`${unitsId}-error`}>
                      {errorFor(`seeded_loads.${row.key}.units`)}
                    </FieldError>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {removed.length > 0 && (
        <ul aria-label="Seeds removed when you save" className="m-0 flex list-none flex-col gap-2 p-0">
          {removed.map(([key, seed]) => (
            <li key={key} className="flex flex-wrap items-center gap-2 text-sm">
              <span className="min-w-0 flex-1">
                Removed when you save: {nameOf(key)} ({formatNumber(seed.load)} {seed.units})
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-11 md:h-8"
                aria-label={`Keep the ${nameOf(key)} seed`}
                onClick={() => keep(key)}
              >
                Keep it
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="rounded-md border border-dashed p-3">
        <p className="m-0 mb-2 text-sm font-medium">Add a seed</p>
        <div className="grid items-start gap-3 sm:grid-cols-[minmax(0,1fr)_8rem_6rem]">
          <div className="flex min-w-0 flex-col gap-1.5">
            <Label htmlFor={newId("exercise")}>
              Exercise<span className="sr-only"> for the new seed</span>
            </Label>
            <NativeSelect
              id={newId("exercise")}
              value={pick}
              onChange={(event) => {
                setPick(event.target.value);
                setAdded("");
              }}
              aria-invalid={addErrors.exercise ? true : undefined}
              aria-describedby={addErrors.exercise ? `${newId("exercise")}-error` : undefined}
              className="h-11 md:h-9"
            >
              <option value="">Pick an exercise…</option>
              {groups.map((group) => (
                <optgroup key={group.role} label={group.label}>
                  {group.options.map((exercise) => (
                    <option key={exercise.key} value={exercise.key}>
                      {exercise.name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </NativeSelect>
            <FieldError id={`${newId("exercise")}-error`}>{addErrors.exercise}</FieldError>
          </div>
          <SettingsField
            id={newId("load")}
            label={
              <>
                {loadWord(byKey[pick])}
                <span className="sr-only"> for the new seed</span>
              </>
            }
            value={load}
            onChange={(value) => {
              setLoad(value);
              setAdded("");
            }}
            placeholder={newUnits === "kg" ? "e.g. 60" : "e.g. 135"}
            error={addErrors.load}
          />
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={newId("units")}>
              Units<span className="sr-only"> for the new seed</span>
            </Label>
            <NativeSelect
              id={newId("units")}
              value={newUnits}
              onChange={(event) => setPickUnits(event.target.value)}
              className="h-11 md:h-9"
            >
              {UNIT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.value}
                </option>
              ))}
            </NativeSelect>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button type="button" variant="outline" className="h-11 md:h-9" onClick={add}>
            <Plus aria-hidden="true" />
            Add seed
          </Button>
          <p className="m-0 text-xs text-muted-foreground" role="status">
            {added || "Added seeds save with the rest when you press Save."}
          </p>
        </div>
      </div>
    </SettingsSection>
  );
}

SeedsSection.propTypes = {
  seeds: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      load: PropTypes.string.isRequired,
      units: PropTypes.string.isRequired,
      set_at: PropTypes.string,
    }),
  ).isRequired,
  settings: PropTypes.object,
  exercises: PropTypes.arrayOf(PropTypes.object),
  units: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  errorFor: PropTypes.func.isRequired,
};

export default SeedsSection;
